import React, { useState, useEffect } from "react";
import Loading from "../Loading";
import { toast } from "react-toastify";
import {
  createNotice,
  getNotices,
  updateNotice,
  deleteNotice,
} from "../../http";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Notices = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState("list"); // 'list' or 'create' or 'edit'
  const [notices, setNotices] = useState([]);
  const [selectedNoticeId, setSelectedNoticeId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noticeToDelete, setNoticeToDelete] = useState(null);
  const [error, setError] = useState(null);

  const [noticeForm, setNoticeForm] = useState({
    title: "",
    content: "",
  });

  useEffect(() => {
    fetchNotices();
    // eslint-disable-next-line
  }, []);

  const fetchNotices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getNotices();
      // The API might return data directly or nested in a data property
      const noticesData = response.data ? response.data : response;
      setNotices(Array.isArray(noticesData) ? noticesData : []);

      if (
        Array.isArray(noticesData) &&
        noticesData.length > 0 &&
        !selectedNoticeId
      ) {
        setSelectedNoticeId(noticesData[0]._id);
      }
    } catch (error) {
      console.error("Error fetching notices:", error);
      setError("Failed to load notices. Please try again.");
      setNotices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNoticeForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const testData = {
        ...noticeForm,
        testEmail: "your-test-email@example.com", // Add this for testing
      };

      if (view === "create") {
        const response = await createNotice(testData);
        console.log("Create Notice Response:", response);
        if (response.success) {
          toast.success("Notice created and emails sent successfully!");
        } else {
          toast.error(
            response.message || "Failed to create notice and send emails"
          );
        }
      } else if (view === "edit" && selectedNoticeId) {
        const response = await updateNotice(selectedNoticeId, noticeForm);
        console.log("Update Notice Response:", response);
        if (response.success) {
          toast.success("Notice updated and emails sent successfully!");
        } else {
          toast.error(
            response.message || "Failed to update notice and send emails"
          );
        }
      }

      // Only fetch notices if the previous operations succeeded
      await fetchNotices();

      setNoticeForm({
        title: "",
        content: "",
      });
      setView("list");
    } catch (error) {
      console.error("Error with notice:", error);
      // More specific error message based on the error response
      const errorMessage =
        error.response?.data?.message ||
        (view === "create"
          ? "Error creating notice and sending emails"
          : "Error updating notice and sending emails");
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditNotice = (notice) => {
    setSelectedNoticeId(notice._id);
    setNoticeForm({
      title: notice.title,
      content: notice.content,
    });
    setView("edit");
  };

  const handleDeleteConfirm = (notice) => {
    setNoticeToDelete(notice);
    setShowDeleteConfirm(true);
  };

  const handleDeleteNotice = async () => {
    if (!noticeToDelete) return;

    setIsLoading(true);
    setError(null);
    try {
      await deleteNotice(noticeToDelete._id);

      // Only update UI after successful API call
      setNotices((prevNotices) =>
        prevNotices.filter((notice) => notice._id !== noticeToDelete._id)
      );

      setShowDeleteConfirm(false);
      setNoticeToDelete(null);

      if (selectedNoticeId === noticeToDelete._id) {
        // Set to the first available notice or null
        const firstAvailableNotice = notices.find(
          (n) => n._id !== noticeToDelete._id
        );
        setSelectedNoticeId(
          firstAvailableNotice ? firstAvailableNotice._id : null
        );
      }
    } catch (error) {
      console.error("Error deleting notice:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error deleting notice. Please try again.";
      setError(errorMessage);
      setShowDeleteConfirm(false); // Close modal even on error
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB").replace(/\//g, "-");
  };

  // Fixed the conditional rendering to safely check for notices
  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="main-content">
      <section className="section">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h4>
              {view === "list"
                ? "Notices"
                : view === "create"
                ? "Create New Notice"
                : "Edit Notice"}
            </h4>
            {view === "list" ? (
              <button
                className="btn btn-primary"
                onClick={() => setView("create")}>
                Create Notice
              </button>
            ) : (
              <button
                className="btn btn-secondary"
                onClick={() => setView("list")}>
                Back to Notices
              </button>
            )}
          </div>

          <div className="card-body">
            {error && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"></button>
              </div>
            )}

            {view === "list" ? (
              notices && notices.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: "25%" }}>Title</th>
                        <th style={{ width: "30%" }}>Content</th>
                        <th style={{ width: "15%" }}>Created</th>
                        <th style={{ width: "15%" }}>Updated</th>
                        <th style={{ width: "15%" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notices.map((notice) => (
                        <tr key={notice._id}>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: "150px" }}>
                            {notice.title}
                          </td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: "200px" }}>
                            {notice.content}
                          </td>
                          <td>{formatDate(notice.createdAt)}</td>
                          <td>
                            {notice.updatedAt
                              ? formatDate(notice.updatedAt)
                              : "-"}
                          </td>
                          <td>
                            <div className="d-flex">
                              <button
                                className="btn btn-primary btn-sm me-1"
                                onClick={() => handleEditNotice(notice)}
                                title="Edit">
                                <i className="fas fa-edit"></i>{" "}
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteConfirm(notice)}
                                title="Delete">
                                <i className="fas fa-trash"></i>{" "}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <p>No notices available</p>
                </div>
              )
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="col-md-9">
                  <div className="form-group mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={noticeForm.title}
                      onChange={handleChange}
                      required
                      maxLength={100}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Content</label>
                    <textarea
                      className="form-control"
                      name="content"
                      value={noticeForm.content}
                      onChange={handleChange}
                      rows="5"
                      required
                    />
                  </div>

                  <div className="form-group mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}>
                      {isLoading
                        ? "Saving..."
                        : view === "create"
                        ? "Create Notice"
                        : "Update Notice"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-light ms-2"
                      onClick={() => setView("list")}
                      disabled={isLoading}>
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && noticeToDelete && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  {/* <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteConfirm(false)}></button> */}
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete the notice "
                    {noticeToDelete.title}"?
                  </p>
                  <p className="text-danger">This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteNotice}
                    disabled={isLoading}>
                    {isLoading ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Notices;
